/**
 * The badge a sidebar nav item can carry.
 *
 * Lifted out of the tenant app with `NavMain` so both apps render one sidebar. A plain string is the common
 * case ("New"); the object form exists for the few that need a tone.
 */
export enum SidebarNavBadgeVariant {
  Success = "success",
  Warning = "warning",
  Info = "info",
  Neutral = "neutral",
}

export type SidebarNavBadge =
  | string
  | {
      label: string;
      variant?: SidebarNavBadgeVariant;
    };

export function getSidebarNavBadgeLabel(badge: SidebarNavBadge): string {
  return typeof badge === "string" ? badge : badge.label;
}

/**
 * Chrome classes shared by every app's sidebar, so the two shells cannot drift apart.
 *
 * `border-r-0` matters: the shell's inset already draws a rounded top-left border, and leaving the sidebar's
 * own right border on puts a second, straight line beside it.
 */
export const sidebarChromeClassName =
  "border-r-0 group-data-[side=left]:border-r-0 group-data-[side=right]:border-l-0";
export const sidebarHeaderClassName = "px-3 pt-3 pb-2 group-data-[collapsible=icon]:px-2";
export const sidebarContentClassName = "pb-3 group-data-[collapsible=icon]:px-0";
export const sidebarFooterClassName =
  "border-sidebar-border/70 border-t px-3 pt-1.5 pb-2 group-data-[collapsible=icon]:px-2";
/** A footer row (My branch, Send feedback, …) — matches the density and hover of a NavMain item. */
export const sidebarFooterItemClassName =
  "h-9 rounded-lg px-2.5 text-[15px] hover:!bg-primary/5 hover:!text-primary data-[active=true]:!bg-primary/10 data-[active=true]:!text-primary dark:hover:!bg-white/5 dark:hover:!text-emerald-300 dark:data-[active=true]:!bg-emerald-400/10 dark:data-[active=true]:!text-emerald-300 [&_svg]:!size-[1.1rem]";
